<template>
  <div>
    <div
      class="requirement-card hover-container"
      :style="{ 'border-color': cardTypeColor }"
      :class="{
        'height-transition-hover-container':
          (item.cardType?.triggerType || item.cardType?.queryType || item.cardType?.aggregateType)
      }"
    >
      <select-entity-combobox
        v-if="showCombobox"
        :events="[event]"
        :cardIds="[item.id]"
        :style="{ '--borderColor': cardTypeColor, '--borderTopWidth': '1px' }"
      />
      <slot v-else>
        <text-editor
          class="pa-1"
          :id="item.id"
          :text="item.description"
          :disabled="$store.state.isWorkflowDisabled"
          @text-update="updateDescription($event, item.id)"
          @text-delta="$emit('text-delta')"
          @move-focus="moveFocus"
          @keydown.native.stop=""
          :borderColor="cardTypeColor"
        />
      </slot>
      <slot name="data-fields" />
      <v-container fluid :class="{ 'pa-0': true, 'mt-0': showAdd, 'mt-3': !showAdd }">
        <v-row class="ma-0 align-center">
          <v-col class="pa-0">
            <Suspense>
              <template #default>
                <requirement-actions
                  :requirement="item"
                  :show-as-invisible="showAsInvisible"
                  :jiraData2="jiraData2"
                  :loadingJiraInfo="loadingJiraInfo"
                  :with-dates="withDates"
                  :date-created="item.created"
                  :date-updated="item.updated"
                  :creator="item.creator"
                  @open-replies="openReplies"
                  :hideTypeSelector="hideTypeSelector"
                  :hideOpenInSidebar="hideOpenInSidebar"
                  :showAdd="showAdd"
                  :hideCreator="hideCreator"
                  :hideReleaseSelector="hideReleaseSelector"
                  :hideActions="hideActions"
                  :showCommentsCount="showCommentsCount"
                ></requirement-actions>
              </template>
              <template #fallback>
                <div class="loading-text">Loading...</div>
              </template>
            </Suspense>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <requirement-replies
      v-if="!hideReplies"
      class="my-2"
      :showAll="showAll"
      :requirementId="item.id"
      :replies="item.comments"
      :expand="repliesOpened || showAll"
      :reply-form-visible="replyFormVisible"
      :with-dates="replyWithDates"
      :hide-avatar="hideAvatar"
      :showAi="showAi"
      :mini-mode="miniMode"
      @expand-change="handleExpandChange"
      @expand-transition-after="$emit('expand-transition-after')"
      @hide-reply-form="replyFormVisible = false"
      @ask-for-reply="$emit('ask-for-reply', $event)"
      @open-replies="openReplies"
    ></requirement-replies>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RequirementReplies from '@/components/RequirementReplies'
import requirementTypesApi from '@/api/requirementTypesApi'
import TextEditor from '@/components/TextEditor.vue'
import workflowApi from '@/api/workflowApi'
import { defineAsyncComponent } from 'vue'
import SelectEntityCombobox from '@/components/SelectEntityCombobox.vue'

export default {
  components: {
    SelectEntityCombobox,
    RequirementReplies,
    TextEditor,
    RequirementActions: defineAsyncComponent(() => import('@/components/RequirementActions'))
  },

  props: {
    item: Object,
    event: Object,
    withDates: Boolean,
    replyWithDates: Boolean,
    jiraData2: Object,
    loadingJiraInfo: Number,
    hideAvatar: Boolean,
    hideTypeSelector: Boolean,
    hideReleaseSelector: Boolean,
    hideOpenInSidebar: Boolean,
    hideActions: Boolean,
    hideReplies: Boolean,
    hideThumbs: Boolean,
    hideComments: Boolean,
    showAi: Boolean,
    showEntitySelector: Boolean,
    showAll: Boolean,
    showAsInvisible: Boolean,
    boundedContexts: Array,
    showCommentsCount: Boolean,
    defaultReplyFormVisible: Boolean,
    miniMode: Boolean,
    showAdd: Boolean,
    hideCreator: Boolean
  },

  data () {
    return {
      repliesOpened: false,
      replyFormVisible: false,
      keyDownPressed: false
    }
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow
    }),
    cardTypeColor () {
      if (this.showAsInvisible) {
        return '#868686'
      }

      const { cardType } = this.item
      return requirementTypesApi.getBackgroundColour(cardType)
    },
    showCombobox () {
      return this.showEntitySelector && (this.item.cardType.aggregateType || this.item.cardType.queryType) && this.event
    }
  },

  methods: {
    moveFocus (direction, target) {
      target.firstChild.blur()
    },
    updateDescription (html, itemId) {
      workflowApi.updateRequirementsJsonMutation(this.$route.params.id, [{ requirementId: itemId, description: html }])
    },
    openReplies () {
      this.$emit('open-replies', this.item.id)
      this.repliesOpened = true
      this.replyFormVisible = true
    },

    handleExpandChange (expand) {
      if (expand) {
        this.repliesOpened = true
      } else {
        this.repliesOpened = false
        this.replyFormVisible = false
      }
    }
  },
  mounted () {
    this.replyFormVisible = this.defaultReplyFormVisible || false
  }
}
</script>

<style scoped>
  .requirement-card {
    overflow: hidden;
    padding: 12px;
    border-radius: 8px;
    background: #FFF;
    border-width: 2px 1px 1px 1px;
    border-style: solid;
    border-color: black;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  }

  .requirement-card-handle {
    width: 24px;
    height: 4px;
    margin-bottom: 9px;
    border-radius: 10px;
    background: black;
  }

  .text-nowrap {
    white-space: nowrap;
  }

  .v-tooltip__content {
    background: white;
    color: var(--color-primary);
    border-radius: 10px;
  }

  .loading-text {
    font-size: 11px;
  }
</style>
